import {useConfiguration} from "@/features/configuration";
import {usePreservedNavigate} from "@/lib/router";
import {useOrderContext} from "../context";

const BASE_PATH = "/taxi";

/**
 * Hook for managing navigation flow in the public taxi order process.
 * Centralizes all navigation logic to avoid spreading it across components.
 * Uses absolute paths to ensure consistent behavior regardless of component location.
 *
 * Flow: pickup -> dropoff -> overview -> confirmation
 */
export function useFlowNavigation() {
    const navigate = usePreservedNavigate();
    const {orderDraft} = useOrderContext();
    const {data: configuration} = useConfiguration();

    const hasFixedPickup = !!configuration?.fixedPickup;
    const hasFixedDestination = !!configuration?.fixedDestination;

    /**
     * Handles navigation after a location (pickup or dropoff) has been selected.
     * Determines the next step based on which location was selected and what data is available.
     * Respects fixed locations configuration.
     *
     * @param type - The type of location that was selected
     */
    const handleLocationSelected = (type: "pickup" | "dropoff") => {
        if (type === "pickup" && !orderDraft.dropoff && !hasFixedDestination) {
            // Pickup selected, but no dropoff yet and dropoff is not fixed -> go to dropoff
            navigate(`${BASE_PATH}/dropoff`, {replace: true});
        } else if (type === "dropoff" && !orderDraft.pickup && !hasFixedPickup) {
            // Dropoff selected, but no pickup yet and pickup is not fixed -> go to pickup
            navigate(`${BASE_PATH}/pickup`, {replace: true});
        } else {
            // Both locations are now available -> go to overview
            navigate(`${BASE_PATH}/overview`, {replace: true});
        }
    };

    /**
     * Navigates to the next appropriate step based on current order draft state.
     * Useful for programmatic navigation or redirects.
     */
    const goToNextStep = () => {
        if (!orderDraft.pickup && !hasFixedPickup) {
            navigate(`${BASE_PATH}/pickup`, {replace: true});
        } else if (!orderDraft.dropoff && !hasFixedDestination) {
            navigate(`${BASE_PATH}/dropoff`, {replace: true});
        } else {
            navigate(`${BASE_PATH}/overview`, {replace: true});
        }
    };

    /**
     * Navigates back to the beginning of the flow.
     */
    const goToStart = () => {
        if (hasFixedPickup && !hasFixedDestination) {
            navigate(`${BASE_PATH}/dropoff`, {replace: true});
        } else {
            navigate(`${BASE_PATH}/pickup`, {replace: true});
        }
    };

    /**
     * Navigates to the confirmation/ride status screen after order creation.
     */
    const goToConfirmation = (orderId: string) => {
        navigate(`${BASE_PATH}/confirmation/${orderId}`, {replace: true});
    };

    return {
        handleLocationSelected,
        goToNextStep,
        goToStart,
        goToConfirmation,
    };
}
